// client/src/features/quotations/rfqUnitMismatch.ts
//
// Owner decision 2026-09-24: "ideally we want to send the quotation in the
// unit they requested. Flag the line items that are not in the same unit as
// asked in the RFQ, and also when the send-quotation button is pressed, so
// users are pushed to use the same unit as asked — but they shouldn't be
// blocked: if they ignore the flag, it's fine."
//
// The ONE owner of "is this line's quoted unit the unit the RFQ asked for".
// `QuotationDetailPage` renders the row tint from this and the Send-quote
// dialog lists its output; neither re-derives the comparison.
//
// Deliberately narrower than `QuotationDetailPage`'s own `askedDiffers` (used
// for the blue "restated" mark on the Requested Qty / Unit cells): that also
// fires on a QUANTITY difference alone. This is UNIT only — a line correctly
// restated to a new quantity in the SAME unit is never flagged here, and a
// line quoted in a different unit at the SAME quantity always is. It is also
// unrelated to `item.unitMismatch` (TECH_DEBT #61: no pack ratio was on file
// to price the asked unit at all) — that is a pricing gap, this is a plain
// "the units don't match" nudge, advisory only, and never gates Send.
//
// `item.originalRequest` is the durable RFQ ask (TECH_DEBT #151, written once
// at generation and never rewritten — the same field
// `QuotationDetailPage.readOriginalRequest` reads). This module reads it
// directly rather than importing that function (page-local, not exported):
// `readOriginalRequest` runs the asked unit through `normalizeUnit`, which
// blanks anything it cannot canonicalise to "" — exactly the spelling the
// raw-string fallback below needs. Nothing here reimplements a normaliser:
// the only local logic is "is there a recorded ask at all", the same
// null/quantity gate `readOriginalRequest` uses; the unit comparison itself is
// entirely `isSameUnit`'s.

import { isSameUnit } from '../../utils/unitVocabulary';
import type { QuotationItemDto } from '../../lib/quotationApi';

/** The minimal shape this needs from a quotation line — matches `QuotationItemDto`. */
export type RfqUnitMismatchLine = Pick<QuotationItemDto, 'originalRequest' | 'quantity' | 'uom'>;

export type RfqUnitMismatch = {
  askedQty: number;
  askedUom: string;
  /** Null when the line carries no quantity yet — missing is not zero (CLAUDE.md invariant 8). */
  quotedQty: number | null;
  quotedUom: string;
};

export type RfqUnitMismatchEntry = RfqUnitMismatch & {
  lineNumber: number;
  itemName: string;
};

/**
 * Null when the line is not flagged: no RFQ ask on file, the ask or the quote
 * carries no unit to compare, or the two units are the same
 * (`isSameUnit` — KGS/KG, CASES/CASE, etc. are one unit, never flagged).
 *
 * Unknown/uncanonicalisable units: `isSameUnit` compares canonical forms when
 * EITHER side canonicalises (so a recognised unit is never "equal" to one the
 * vocabulary does not know), and falls back to comparing the raw spellings
 * case-insensitively only when NEITHER side canonicalises. That fallback is
 * why this function hands it the RAW `originalRequest.uom` / `uom` text
 * rather than a pre-normalised one — see the file header.
 */
export function findRfqUnitMismatch(line: RfqUnitMismatchLine): RfqUnitMismatch | null {
  const req = line.originalRequest;
  if (!req || typeof req !== 'object') return null;

  const askedQty = typeof req.quantity === 'number' && Number.isFinite(req.quantity) ? req.quantity : null;
  if (askedQty == null) return null;

  const askedUom = typeof req.uom === 'string' ? req.uom.trim() : '';
  if (!askedUom) return null; // The RFQ recorded no unit for this line — nothing to compare.

  const quotedUom = typeof line.uom === 'string' ? line.uom.trim() : '';
  if (!quotedUom) return null; // The line carries no unit yet — nothing to compare.

  if (isSameUnit(askedUom, quotedUom)) return null;

  // Missing is not zero (CLAUDE.md invariant 8, F10 2026-09-25): a line with no
  // quantity yet is not "quoted 0 CASE" — that read as a real, if odd, amount
  // rather than as nothing having been entered. `null` here is what lets a
  // renderer say "quoted in CASE" instead.
  const quotedQty =
    typeof line.quantity === 'number' && Number.isFinite(line.quantity)
      ? line.quantity
      : Number.isFinite(Number(line.quantity)) && String(line.quantity ?? '').trim() !== ''
        ? Number(line.quantity)
        : null;

  return { askedQty, askedUom, quotedQty, quotedUom };
}

/**
 * Every mismatched line in a quotation, in line-number order — what the
 * Send-quote dialog lists and what "Go fix" steps through, one per press (`nextMismatchLine`, D101.3).
 */
export function collectRfqUnitMismatches(
  items: ReadonlyArray<Pick<QuotationItemDto, 'originalRequest' | 'quantity' | 'uom' | 'lineNumber' | 'itemName'>>
): RfqUnitMismatchEntry[] {
  const out: RfqUnitMismatchEntry[] = [];
  for (const item of items || []) {
    const mismatch = findRfqUnitMismatch(item);
    if (mismatch) out.push({ ...mismatch, lineNumber: item.lineNumber, itemName: item.itemName || '' });
  }
  return out;
}

/**
 * The line "Go fix" goes to next (D101.3): the first entry after `lastLine` (the line it went to
 * last, or null), wrapping to the first. `entries` are in line order. A `lastLine` that is no
 * longer listed (that line got fixed) still lands on the next higher one.
 */
export function nextMismatchLine(entries: ReadonlyArray<Pick<RfqUnitMismatchEntry, 'lineNumber'>>, lastLine: number | null): number | null {
  if (!entries.length) return null;
  const after = lastLine == null ? undefined : entries.find((e) => e.lineNumber > lastLine);
  return (after ?? entries[0]).lineNumber;
}

/**
 * The "quoted ..." half of the mismatch sentence — the ONE place that reads
 * `quotedQty`, so the missing-quantity case (F10, 2026-09-25) is worded once
 * for every caller: `QuotationDetailPage`'s row tooltip and
 * `RfqUnitMismatchDialog`'s per-line text both call this rather than each
 * template-stringing `quotedQty` themselves.
 *
 * Missing is not zero (CLAUDE.md invariant 8): a line with no quantity yet
 * used to be coerced to 0 and read "quoted 0 CASE" — a real, if strange,
 * amount rather than "nothing entered". `null` states the unit alone instead.
 */
export function describeQuotedAmount(m: Pick<RfqUnitMismatch, 'quotedQty' | 'quotedUom'>): string {
  return m.quotedQty == null ? `quoted in ${m.quotedUom}` : `quoted ${m.quotedQty} ${m.quotedUom}`;
}
